import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";

import AddIcon from "@material-ui/icons/Add";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ReplyOutlinedIcon from "@material-ui/icons/ReplyOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import PhoneIcon from "@material-ui/icons/Phone";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";

import { i18n } from "../../translate/i18n";
import { getTokens } from "../../styles/tokens";
import {
  BUTTON_TYPES,
  LIMITS,
  emptyButton,
  extractVariables,
} from "../../pages/MetaTemplates/templateUtils";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    head: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      flexWrap: "wrap",
      marginBottom: 12,
    },
    counter: { color: t.textMuted, fontSize: 12, fontVariantNumeric: "tabular-nums" },
    list: { display: "flex", flexDirection: "column", gap: 10 },
    item: {
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      background: t.muted,
      padding: "10px 14px 14px",
    },
    itemHead: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      marginBottom: 8,
    },
    itemType: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13,
      fontWeight: 600,
      color: t.text,
      flex: 1,
      minWidth: 0,
      "& svg": { fontSize: 18, color: t.textSecondary },
    },
    itemIndex: { color: t.textMuted, fontWeight: 500, fontVariantNumeric: "tabular-nums" },
    // classe simples: perde para o estado desabilitado do MUI (que continua cinza)
    action: { color: t.textSecondary },
    remove: { color: t.semantic.error },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 12,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    full: { gridColumn: "1 / -1" },
    field: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
    labelRow: { display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 },
    label: { fontSize: 12.5, fontWeight: 600, color: t.textSecondary },
    fieldCounter: {
      marginLeft: "auto",
      flex: "none",
      color: t.textMuted,
      fontSize: 12,
      fontVariantNumeric: "tabular-nums",
    },
    help: { fontSize: 12, lineHeight: 1.5, color: t.textMuted },
    menuIcon: { marginRight: 10, fontSize: 18, color: t.textSecondary },
    empty: {
      border: `1px dashed ${t.lineStrong}`,
      borderRadius: t.radius.sm,
      padding: "14px 16px",
      color: t.textSecondary,
      fontSize: 13,
      lineHeight: 1.5,
    },
  };
});

/** Rótulo acima do campo (contador à direita) e ajuda curta abaixo. */
const Field = ({ classes, label, id, counter, help, className, children }) => (
  <div className={`${classes.field}${className ? ` ${className}` : ""}`}>
    <div className={classes.labelRow}>
      <label htmlFor={id} className={classes.label}>
        {label}
      </label>
      {counter != null && <span className={classes.fieldCounter}>{counter}</span>}
    </div>
    {children}
    {help && <div className={classes.help}>{help}</div>}
  </div>
);

const ICONS = {
  QUICK_REPLY: ReplyOutlinedIcon,
  URL: OpenInNewIcon,
  PHONE_NUMBER: PhoneIcon,
  COPY_CODE: FileCopyOutlinedIcon,
};

const MAX_BY_TYPE = {
  QUICK_REPLY: LIMITS.buttons,
  URL: LIMITS.urlButtons,
  PHONE_NUMBER: LIMITS.phoneButtons,
  COPY_CODE: LIMITS.copyCodeButtons,
};

/**
 * Lista editável de botões do template (até 10): resposta rápida, URL,
 * telefone e copiar código; reordenar e remover.
 */
const ButtonsEditor = ({ buttons, onChange, disabled }) => {
  const classes = useStyles();
  const [anchor, setAnchor] = useState(null);
  const list = Array.isArray(buttons) ? buttons : [];

  const countOf = (type) => list.filter((b) => b.type === type).length;
  const canAdd = (type) =>
    list.length < LIMITS.buttons && countOf(type) < MAX_BY_TYPE[type];

  const update = (idx, patch) => {
    onChange(list.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  };
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= list.length) return;
    const next = [...list];
    const [item] = next.splice(idx, 1);
    next.splice(to, 0, item);
    onChange(next);
  };
  const add = (type) => {
    setAnchor(null);
    if (!canAdd(type)) return;
    onChange([...list, emptyButton(type)]);
  };

  const t = (k, o) => i18n.t(`metaTemplates.editor.buttons.${k}`, o);

  const renderFields = (b, idx) => {
    const fid = (name) => `mt-btn-${idx}-${name}`;
    if (b.type === "COPY_CODE") {
      return (
        <Field
          classes={classes}
          label={t("copyCodeExample")}
          id={fid("code")}
          counter={`${(b.example || "").length}/${LIMITS.copyCode}`}
          help={t("copyCodeHelp")}
        >
          <TextField
            id={fid("code")}
            value={b.example || ""}
            onChange={(e) => update(idx, { example: e.target.value })}
            inputProps={{ maxLength: LIMITS.copyCode }}
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
          />
        </Field>
      );
    }
    const textField = (
      <Field
        classes={classes}
        label={t("text")}
        id={fid("text")}
        counter={`${(b.text || "").length}/${LIMITS.buttonText}`}
      >
        <TextField
          id={fid("text")}
          value={b.text || ""}
          onChange={(e) => update(idx, { text: e.target.value })}
          inputProps={{ maxLength: LIMITS.buttonText }}
          variant="outlined"
          size="small"
          fullWidth
          disabled={disabled}
        />
      </Field>
    );
    if (b.type === "QUICK_REPLY") return textField;
    if (b.type === "PHONE_NUMBER") {
      return (
        <div className={classes.grid}>
          {textField}
          <Field classes={classes} label={t("phone")} id={fid("phone")} help={t("phoneHelp")}>
            <TextField
              id={fid("phone")}
              value={b.phone_number || ""}
              onChange={(e) => update(idx, { phone_number: e.target.value })}
              placeholder="+5511999999999"
              variant="outlined"
              size="small"
              fullWidth
              disabled={disabled}
            />
          </Field>
        </div>
      );
    }
    const hasVar = extractVariables(b.url).length > 0;
    return (
      <div className={classes.grid}>
        {textField}
        <Field classes={classes} label={t("url")} id={fid("url")} help={t("urlHelp")}>
          <TextField
            id={fid("url")}
            value={b.url || ""}
            onChange={(e) => update(idx, { url: e.target.value })}
            placeholder="https://exemplo.com.br/fatura/{{1}}"
            variant="outlined"
            size="small"
            fullWidth
            disabled={disabled}
          />
        </Field>
        {hasVar && (
          <Field
            classes={classes}
            className={classes.full}
            label={t("urlExample")}
            id={fid("url-example")}
            help={t("urlExampleHelp")}
          >
            <TextField
              id={fid("url-example")}
              value={b.example || ""}
              onChange={(e) => update(idx, { example: e.target.value })}
              placeholder="https://exemplo.com.br/fatura/123"
              variant="outlined"
              size="small"
              fullWidth
              disabled={disabled}
            />
          </Field>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className={classes.head}>
        <span className={classes.counter}>
          {t("count", { total: list.length, max: LIMITS.buttons })}
        </span>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={(e) => setAnchor(e.currentTarget)}
          disabled={disabled || list.length >= LIMITS.buttons}
        >
          {t("add")}
        </Button>
        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
          {BUTTON_TYPES.map((type) => {
            const Icon = ICONS[type];
            return (
              <MenuItem key={type} onClick={() => add(type)} disabled={!canAdd(type)}>
                <Icon className={classes.menuIcon} />
                {t(`type.${type}`)}
              </MenuItem>
            );
          })}
        </Menu>
      </div>

      {list.length === 0 && <div className={classes.empty}>{t("empty")}</div>}

      <div className={classes.list}>
        {list.map((b, idx) => {
          const Icon = ICONS[b.type] || ReplyOutlinedIcon;
          return (
            <div key={idx} className={classes.item}>
              <div className={classes.itemHead}>
                <div className={classes.itemType}>
                  <span className={classes.itemIndex}>{idx + 1}.</span>
                  <Icon />
                  <span>{t(`type.${b.type}`)}</span>
                </div>
                <Tooltip title={t("moveUp")}>
                  <span>
                    <IconButton
                      size="small"
                      className={classes.action}
                      onClick={() => move(idx, -1)}
                      disabled={disabled || idx === 0}
                      aria-label={t("moveUp")}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t("moveDown")}>
                  <span>
                    <IconButton
                      size="small"
                      className={classes.action}
                      onClick={() => move(idx, 1)}
                      disabled={disabled || idx === list.length - 1}
                      aria-label={t("moveDown")}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t("remove")}>
                  <span>
                    <IconButton
                      size="small"
                      className={classes.remove}
                      onClick={() => remove(idx)}
                      disabled={disabled}
                      aria-label={t("remove")}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
              {renderFields(b, idx)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ButtonsEditor;
